import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import './Radio.css';

/**
 * Radio Component
 * Styled radio button with label
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Radio button label
 * @param {boolean} props.checked - Checked state
 * @param {Function} props.onChange - Change handler
 * @param {string} props.value - Radio value
 * @param {string} props.name - Radio group name
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.className - Additional CSS classes
 */
const Radio = forwardRef(
  (
    {
      label,
      checked = false,
      onChange,
      value,
      name,
      disabled = false,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const radioId = id || `radio-${Math.random().toString(36).substr(2, 9)}`;

    const containerClasses = `
      radio-container
      ${disabled ? 'radio-container--disabled' : ''}
      ${className}
    `.trim().replace(/\s+/g, ' ');

    return (
      <div className={containerClasses}>
        <label htmlFor={radioId} className="radio-label-wrapper">
          <input
            ref={ref}
            type="radio"
            id={radioId}
            name={name}
            value={value}
            className="radio-input"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            {...props}
          />

          <motion.div
            className={`radio-custom ${checked ? 'radio-custom--checked' : ''}`}
            animate={{ scale: checked ? [1, 1.1, 1] : 1 }}
            transition={{ duration: 0.2 }}
          >
            {checked && (
              <motion.div
                className="radio-dot"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.2 }}
              />
            )}
          </motion.div>

          {label && <span className="radio-label">{label}</span>}
        </label>
      </div>
    );
  }
);

Radio.displayName = 'Radio';

Radio.propTypes = {
  label: PropTypes.string,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
};

export default Radio;
